import * as React from "react"
import { Calendar as CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"

const inputBaseClass = "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"

function isoToDisplay(iso) {
  if (!iso || typeof iso !== "string") return ""
  const m = iso.split("T")[0].match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return ""
  return `${m[3]}/${m[2]}/${m[1]}`
}

function displayToIso(display) {
  const m = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (!m) return null
  const [, dd, mm, yyyy] = m
  const d = parseInt(dd, 10), mo = parseInt(mm, 10), y = parseInt(yyyy, 10)
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || y < 1000) return null
  return `${yyyy}-${mm}-${dd}`
}

// Input data sempre in formato italiano gg/mm/aaaa, indipendentemente dal
// locale del sistema operativo/browser di chi lo usa: un <input type="date">
// nativo mostra mm/gg/aaaa su un browser configurato in inglese US, cosa
// che non si puo' correggere via CSS/JS. Qui il testo visibile e' sempre
// gg/mm/aaaa; il contratto value/onChange resta identico a prima (stringa
// ISO YYYY-MM-DD), quindi tutte le pagine che usano <Input type="date">
// continuano a funzionare senza modifiche.
const DateInput = React.forwardRef(({ value, onChange, className, disabled, placeholder, ...props }, ref) => {
  const [text, setText] = React.useState(() => isoToDisplay(value))
  const hiddenRef = React.useRef(null)
  const isoValue = value || ""

  React.useEffect(() => {
    setText(isoToDisplay(value))
  }, [value])

  const fireChange = (iso) => {
    if (onChange) onChange({ target: { value: iso } })
  }

  const handleTextChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 8)
    let formatted = digits
    if (digits.length > 4) formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
    else if (digits.length > 2) formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`
    setText(formatted)
    if (digits.length === 8) {
      const iso = displayToIso(formatted)
      fireChange(iso || "")
    } else if (digits.length === 0) {
      fireChange("")
    }
  }

  const handleBlur = (e) => {
    if (text && !displayToIso(text)) setText(isoToDisplay(value))
    if (props.onBlur) props.onBlur(e)
  }

  const openPicker = () => {
    const el = hiddenRef.current
    if (!el || disabled) return
    if (typeof el.showPicker === "function") {
      try { el.showPicker(); return } catch { /* fallback sotto */ }
    }
    el.focus()
  }

  return (
    <div className="relative">
      <input
        ref={ref}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder || "gg/mm/aaaa"}
        value={text}
        onChange={handleTextChange}
        disabled={disabled}
        className={cn(inputBaseClass, "pr-8", className)}
        {...props}
        onBlur={handleBlur}
      />
      <button
        type="button"
        onClick={openPicker}
        disabled={disabled}
        tabIndex={-1}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground disabled:opacity-50"
        aria-label="Apri calendario"
      >
        <CalendarIcon size={14} />
      </button>
      <input
        ref={hiddenRef}
        type="date"
        value={isoValue}
        onChange={(e) => fireChange(e.target.value)}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
        className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
      />
    </div>
  )
})
DateInput.displayName = "DateInput"

const Input = React.forwardRef(({ className, type, ...props }, ref) => {
  if (type === "date") {
    return <DateInput ref={ref} className={className} {...props} />
  }
  return (
    <input
      type={type}
      className={cn(
        inputBaseClass,
        className
      )}
      ref={ref}
      {...props} />
  );
})
Input.displayName = "Input"

export { Input }
